import { getJWTToken } from '../../utils/auth';
import type { PlanSignOff, PlanSignOffValue, RosterRow } from './rosterRows';
import { authHeaders } from '../../core/impersonation';
import { parseRosterMeta, type RosterMeta } from '../../core/rosterMeta';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

const EXITED_STANDINGS = ['reclassified', 'inactive', 'exit_undated'];

export function shownElStatus(row: Pick<RosterClassificationRow, 'elStatus' | 'standing'>): string {
  if (row.standing === 'monitoring') return 'monitoring';
  if (row.standing && EXITED_STANDINGS.includes(row.standing)) return 'former';
  if (row.elStatus === 'past_monitoring') return 'former';
  return row.elStatus;
}

export type RosterClassificationRow = {
  studentKey: string;
  widaKey?: string;
  altKeys: string[];
  elStatus: string;
  inProgram: boolean;
  isLtel: boolean;
  isNewcomer: boolean;
  nearExit?: boolean;
  duallyIdentified: boolean;
  yearsInProgram: number;
  dataWarnings?: string[];
  firstName?: string;
  lastName?: string;
  localId?: string;
  elTeachers?: string[];
  school?: string;
  grade?: string;
  programModel?: string;
  alternateAccess?: boolean;
  // Which assessment compositeProficiency and domainProficiency are on. Absent
  // means ACCESS. An alternate level is 1-5 and is not equivalent to an ACCESS
  // one, so a surface rendering the number has to be told which it has.
  assessmentScale?: string;
  compositeProficiency?: number | null;
  domainProficiency?: Record<string, number>;
  enrollment?: string;
  statusStated?: boolean;
  standing?: string;
  exitReason?: string;
  exitSchoolYearEnd?: number;
  rosterSource?: string;
  heldOut?: boolean;
  heldOutAccess?: boolean;
};

export type ClassificationIndex = Map<string, RosterClassificationRow>;

export type RosterClassificationResult = {
  students: RosterClassificationRow[];
  roster: RosterMeta | null;
  lastUploadedAt: string | null;
};

const REQUIRED_FLAGS = ['inProgram', 'isLtel', 'isNewcomer', 'duallyIdentified'] as const;

// The fields the projections read as typed. Leaving one unchecked only moves
// the schema mismatch to a .map or a .toFixed inside a projection, past the
// point where returning null would have kept the local derivation.
const OPTIONAL_STRINGS = [
  'standing',
  'widaKey',
  'firstName',
  'lastName',
  'localId',
  'school',
  'grade',
  'programModel',
  'assessmentScale',
  'exitReason',
  'rosterSource',
] as const;

function isStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isNumberRecord(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  return Object.values(value).every((entry) => typeof entry === 'number');
}

function optional(value: unknown, ok: (v: unknown) => boolean): boolean {
  return value === undefined || ok(value);
}

export function isClassificationRow(value: unknown): value is RosterClassificationRow {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  if (typeof row.studentKey !== 'string' || row.studentKey === '') return false;
  if (typeof row.elStatus !== 'string') return false;
  if (typeof row.yearsInProgram !== 'number') return false;
  if (row.altKeys !== undefined && !isStringArray(row.altKeys)) return false;
  if (!optional(row.dataWarnings, isStringArray)) return false;
  if (!OPTIONAL_STRINGS.every((field) => optional(row[field], (v) => typeof v === 'string'))) {
    return false;
  }
  if (!optional(row.elTeachers, isStringArray)) return false;
  if (!optional(row.alternateAccess, (v) => typeof v === 'boolean')) return false;
  if (!optional(row.statusStated, (v) => typeof v === 'boolean')) return false;
  if (!optional(row.heldOut, (v) => typeof v === 'boolean')) return false;
  if (!optional(row.heldOutAccess, (v) => typeof v === 'boolean')) return false;
  if (!optional(row.enrollment, (v) => v === 'active' || v === 'departed')) return false;
  if (!optional(row.exitSchoolYearEnd, (v) => typeof v === 'number')) return false;
  if (!optional(row.domainProficiency, isNumberRecord)) return false;
  // A student with no scored administration has no composite, and the server
  // states that as null rather than leaving the field out.
  if (!optional(row.compositeProficiency, (v) => v === null || typeof v === 'number')) {
    return false;
  }
  return REQUIRED_FLAGS.every((flag) => typeof row[flag] === 'boolean');
}

export async function fetchRosterClassification(): Promise<RosterClassificationRow[] | null> {
  const result = await fetchRosterClassificationResult();
  return result === null ? null : result.students;
}

type ClassificationBody = {
  students?: unknown;
  roster?: unknown;
  lastUploadedAt?: unknown;
  rosterUrl?: unknown;
} | null;

export function parseRosterClassificationResult(body: ClassificationBody): RosterClassificationResult | null {
  const students = body?.students;
  if (!Array.isArray(students)) return null;
  // One malformed row means a schema mismatch, not one bad student, so the
  // whole payload is rejected and the local derivation stands.
  if (!students.every(isClassificationRow)) return null;
  return {
    students,
    roster: parseRosterMeta(body?.roster),
    lastUploadedAt: typeof body?.lastUploadedAt === 'string' ? body.lastUploadedAt : null,
  };
}

export async function followRosterHandoff(body: ClassificationBody): Promise<ClassificationBody> {
  if (Array.isArray(body?.students) || typeof body?.rosterUrl !== 'string') return body;
  const handed = await fetch(body.rosterUrl);
  if (!handed.ok) return null;
  return (await handed.json()) as ClassificationBody;
}

export async function fetchRosterClassificationResult(): Promise<RosterClassificationResult | null> {
  try {
    const token = await getJWTToken();
    const res = await fetch(`${apiUrl}/roster/classification`, {
      headers: token ? authHeaders(token) : undefined,
    });
    if (!res.ok) return null;
    const body = await followRosterHandoff((await res.json()) as ClassificationBody);
    return parseRosterClassificationResult(body);
  } catch {
    return null;
  }
}

export function classificationResultOf(
  value: RosterClassificationRow[] | RosterClassificationResult | null,
): RosterClassificationResult | null {
  if (value === null) return null;
  return Array.isArray(value) ? { students: value, roster: null, lastUploadedAt: null } : value;
}

export function indexClassifications(students: RosterClassificationRow[]): ClassificationIndex {
  const index: ClassificationIndex = new Map();
  for (const student of students) {
    index.set(student.studentKey, student);
  }
  for (const student of students) {
    for (const alt of student.altKeys ?? []) {
      if (!index.has(alt)) index.set(alt, student);
    }
  }
  return index;
}

// The server may only ADD the cohorts the browser cannot derive. It must never
// override LTEL, newcomer, dually-identified or EL status: those come from the
// district's configured thresholds and its SIS newcomer flag, neither of which
// this endpoint receives, so its verdict would be a default overwriting a
// setting — and would disagree with the PE report, which IS given them.
export function applyClassifications(rows: RosterRow[], index: ClassificationIndex): RosterRow[] {
  if (index.size === 0) return rows;
  return rows.map((row) => {
    const server = index.get(row.id);
    // The client's own view of who is in the program wins, on both axes it knows
    // better: the server assembles every upload so it cannot see a newest-snapshot
    // departure, and deriveCohorts already gives monitoring and former ELs no
    // cohorts at all, so adding one here would contradict that rule.
    if (row.enrollment !== 'active' || row.elStatus !== 'active') return row;
    // Where the district stated no EL status, this row's 'active' is the
    // browser's fallback, and the server has read the exit dates it could not.
    if (server && (server.standing === 'unreadable' || server.standing === 'no_signal')) return row;
    if (server && !server.inProgram && row.statusInferred) {
      // Out of the program is not the same as gone from it: a monitoring
      // student is neither active nor former.
      const shown = shownElStatus(server);
      const elStatus = shown === 'monitoring' || shown === 'waived' ? shown : 'former';
      return { ...row, elStatus, cohorts: [] };
    }
    return row;
  });
}

export type PlanSignOffsResult = {
  year: string;
  generated: boolean;
  students: Record<string, PlanSignOff>;
};

const PLAN_SIGN_OFF_VALUES: readonly PlanSignOffValue[] = ['not_complete', 'complete', 'not_finalized'];

export function isPlanSignOff(value: unknown): value is PlanSignOff {
  if (typeof value !== 'object' || value === null) return false;
  const status = value as Record<string, unknown>;
  return (
    PLAN_SIGN_OFF_VALUES.includes(status.value as PlanSignOffValue) &&
    typeof status.signed === 'number' &&
    typeof status.required === 'number'
  );
}

export async function fetchPlanSignOffs(): Promise<PlanSignOffsResult | null> {
  try {
    const token = await getJWTToken();
    const res = await fetch(`${apiUrl}/roster/plan-sign-offs`, {
      headers: token ? authHeaders(token) : undefined,
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { year?: unknown; generated?: unknown; students?: unknown } | null;
    if (typeof body?.students !== 'object' || body.students === null) return null;
    const students: Record<string, PlanSignOff> = {};
    for (const [id, status] of Object.entries(body.students as Record<string, unknown>)) {
      if (!isPlanSignOff(status)) return null;
      students[id] = { value: status.value, signed: status.signed, required: status.required };
    }
    return {
      year: typeof body.year === 'string' ? body.year : '',
      generated: body.generated === true,
      students,
    };
  } catch {
    return null;
  }
}
